import { PaginationReturn } from '@common/types/service';
import { PlusAccordionSummary } from '@components/display/accordions/PlusAccordionSummary';
import { GuestInvitationAnalytic, GuestRegistrationAnalytic } from '@event/components/display/accordions';
import { DEFAULT_EVENT_ANALYTICS, DEFAULT_VIRTUAL_ANALYTICS } from '@event/constants';
import { useEventDashboardContext } from '@event/contexts';
import { useGetEventSubscribersActivityQuery, useGetEventSubscribersByStatusQuery } from '@event/services';
import { EventTypes, IEventDetailAccordionProps, ISubscriberActivity } from '@event/types';
import { convertDataToPercentage } from '@event/utils';
import EventSeatOutlinedIcon from '@mui/icons-material/EventSeatOutlined';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import {
    Accordion,
    AccordionDetails,
    Avatar,
    Card,
    CardContent,
    CircularProgress,
    Grow,
    SvgIconOwnProps,
    Typography,
    TypographyOwnProps,
} from '@mui/material';
import { stringAvatar } from '@utils/common';
import { getRoleName } from '@utils/user';
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from 'chart.js';
import clsx from 'clsx';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

ChartJS.register(ArcElement, Tooltip, Legend);

const getActivityState = (status: ISubscriberActivity['status']) => {
    switch (status) {
        case 'accepted':
            return {
                label: 'Registered',
                color: 'alert-info.main',
                bgColor: 'bg-alert-info-bg',
            };
        case 'declined':
        case 'canceled':
            return {
                label: 'Unregistered',
                color: 'dark-red.main',
                bgColor: 'bg-light-error',
            };
        case 'pending':
            return {
                label: 'Pending',
                color: 'dark-yellow.main',
                bgColor: 'bg-warning-light',
            };
        case 'able_to_register':
            return {
                label: 'Able to Register',
                color: 'dark-yellow.main',
                bgColor: 'bg-warning-light',
            };
        case 'on_waiting_list':
            return {
                label: 'In Queue',
                color: 'dark-yellow.main',
                bgColor: 'bg-warning-light',
            };
    }
};

const EventAnalyticsAccordion = ({ handleAccordionChange, isExpanded }: IEventDetailAccordionProps) => {
    const [checked, setChecked] = useState(false);

    const { type, eventId } = useParams();
    const eventType = type as EventTypes;
    const { event, statistics, badges } = useEventDashboardContext();

    const { data: statuses, isFetching: isStatusesFetching } = useGetEventSubscribersByStatusQuery(
        {
            id: Number(eventId),
            type: eventType,
        },
        {
            selectFromResult: (res) => ({
                ...res,
                data: convertDataToPercentage(
                    res.data
                        ?.filter((x) => !['able_to_register', 'on_waiting_list'].includes(x.status))
                        .map((x) => ({ ...x, name: x.status })) ?? [],
                    10
                ),
            }),
        }
    );

    const { data: guestActivityData, isFetching: isActivityFetching } = useGetEventSubscribersActivityQuery({
        id: Number(eventId),
        type: eventType,
        page: 1,
        page_size: 5,
        ordering: '-status_updated_at',
    });
    const guestActivity = guestActivityData as PaginationReturn<ISubscriberActivity> | undefined;

    const accepted = useMemo(() => statuses?.find((x) => x.name === 'accepted'), [statuses]);
    const sum = useMemo(() => (statuses ? statuses.reduce((acc, curr) => acc + curr.count, 0) : 0), [statuses]);
    const totalRegistrations = useMemo(
        () =>
            convertDataToPercentage(
                [
                    { name: 'Registered', count: accepted?.count || 0, color: '#005FBE' },
                    ...[
                        (event?.max_capacity || 0) - (accepted?.count || 0) > 0
                            ? {
                                  name: 'Remaining Seats',
                                  count: (event?.max_capacity || 0) - (accepted?.count || 0),
                                  color: 'transparent',
                              }
                            : null,
                    ].filter((x) => !!x),
                ],
                10
            ),
        [accepted?.count, event?.max_capacity]
    );
    const seatsAvailable = (event?.max_capacity || 0) - (accepted?.count || 0);
    const seatsAvailablePercentage = Math.round(((seatsAvailable * 100) / (event?.max_capacity || 1)) * 100) / 100;
    const seatsColor: {
        text: TypographyOwnProps['color'];
        background: string;
        icon: SvgIconOwnProps['color'];
    } = useMemo(() => {
        if (seatsAvailablePercentage <= 30) {
            return {
                text: 'light-error.contrastText',
                background: 'bg-light-error',
                icon: 'dark-red',
            };
        } else if (seatsAvailablePercentage >= 31 && seatsAvailablePercentage <= 69) {
            return {
                text: 'dark-yellow.main',
                background: 'bg-warning-light',
                icon: 'dark-yellow',
            };
        } else {
            return {
                text: 'light-confirm.contrastText',
                background: 'bg-light-confirm',
                icon: 'dark-green',
            };
        }
    }, [seatsAvailablePercentage]);
    const guestInvitationsLegend = useMemo(() => {
        if (statuses?.length) {
            return statuses;
        } else {
            return [EventTypes.PRIVATE_ONSITE, EventTypes.PUBLIC_ONSITE].includes(eventType)
                ? DEFAULT_EVENT_ANALYTICS
                : DEFAULT_VIRTUAL_ANALYTICS;
        }
    }, [eventType, statuses]);

    const hasRequiredBadges = useMemo(
        () => (badges?.required.data ? badges.required.data.length > 0 : false),
        [badges?.required?.data]
    );

    return (
        <Accordion className="mb-2" expanded={isExpanded} onChange={handleAccordionChange}>
            <PlusAccordionSummary isExpanded={isExpanded}>
                <Typography variant="h5" fontFamily="museo-sans">
                    Event Analytics
                </Typography>
            </PlusAccordionSummary>
            <AccordionDetails className="py-4">
                <div className="grid grid-cols-3 min-[1600px]:grid-cols-5 gap-4 min-h-[200px] mb-4">
                    <Card className="rounded-lg col-span-2">
                        <CardContent className="flex flex-col items-center relative">
                            <Typography fontFamily="museo-sans" letterSpacing={-1} fontSize={20}>
                                Guest Invitations
                            </Typography>
                            {!isStatusesFetching && event ? (
                                <>
                                    <div className="relative w-fit mt-5">
                                        <GuestInvitationAnalytic setChecked={setChecked} statuses={statuses} />
                                        <div className="absolute-center top-[60%]">
                                            <Grow in={checked}>
                                                <div className="flex flex-col items-center">
                                                    <div className="bg-primary-50 rounded-full p-2">
                                                        <ForwardToInboxIcon color="primary-600" />
                                                    </div>
                                                    <Typography
                                                        fontWeight={600}
                                                        fontSize={30}
                                                        color="gray.main"
                                                        fontFamily="museo-sans"
                                                        letterSpacing={0}
                                                    >
                                                        {sum ? sum : 'N/A'}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="gray.main"
                                                        fontFamily="museo-sans"
                                                        letterSpacing={0}
                                                    >
                                                        All Invitees
                                                    </Typography>
                                                </div>
                                            </Grow>
                                        </div>
                                    </div>
                                    <Grow in={checked}>
                                        <div className="flex gap-5 mt-4 px-2">
                                            {guestInvitationsLegend.map((stat) => (
                                                <div key={`stat-${stat.status}`} className="flex items-center gap-1">
                                                    <div
                                                        className={clsx('min-w-[18px] h-[18px] rounded-md', {
                                                            'bg-confirm-500': stat.status === 'accepted',
                                                            'bg-warning-500': ['able_to_register', 'pending'].includes(
                                                                stat.status
                                                            ),
                                                            'bg-error-500': ['canceled', 'declined'].includes(
                                                                stat.status
                                                            ),
                                                        })}
                                                    />
                                                    <Typography fontWeight={700} variant="body1">
                                                        {stat.count}{' '}
                                                        <Typography component="span">
                                                            {getRoleName(stat.status)}
                                                        </Typography>
                                                    </Typography>
                                                </div>
                                            ))}
                                        </div>
                                    </Grow>
                                </>
                            ) : (
                                <CircularProgress className="absolute top-1/2" />
                            )}
                        </CardContent>
                    </Card>
                    <Card className="rounded-lg col-span-1 min-w-[230px]">
                        <CardContent className="flex flex-col items-center relative">
                            <Typography fontFamily="museo-sans" letterSpacing={-1} fontSize={20}>
                                Guest Registrations
                            </Typography>
                            {!isStatusesFetching && !!event ? (
                                <>
                                    <div className="relative w-fit mt-5">
                                        <GuestRegistrationAnalytic totalRegistrations={totalRegistrations} />
                                        <div className="absolute-center top-[60%]">
                                            <Grow in={checked}>
                                                <div className="flex flex-col items-center">
                                                    <div className="bg-primary-50 rounded-full p-2">
                                                        <PeopleAltOutlinedIcon color="primary-600" />
                                                    </div>
                                                    <Typography
                                                        fontWeight={600}
                                                        fontSize={30}
                                                        color="gray.main"
                                                        fontFamily="museo-sans"
                                                        letterSpacing={0}
                                                    >
                                                        {accepted?.count
                                                            ? `${totalRegistrations[0].count}/${event?.max_capacity}`
                                                            : 'N/A'}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="gray.main"
                                                        fontFamily="museo-sans"
                                                        letterSpacing={0}
                                                    >
                                                        Registered
                                                    </Typography>
                                                </div>
                                            </Grow>
                                        </div>
                                    </div>
                                    <Grow in={checked}>
                                        <div
                                            className={clsx(
                                                'flex gap-3 items-center w-[191px] justify-center rounded-lg py-1',
                                                seatsColor.background
                                            )}
                                        >
                                            <EventSeatOutlinedIcon fontSize="large" color={seatsColor.icon} />
                                            <Typography color={seatsColor.text} variant="h4" fontWeight={700}>
                                                {seatsAvailable}
                                            </Typography>
                                            <Typography
                                                color={seatsColor.text}
                                                variant="body2"
                                                fontWeight={500}
                                                lineHeight={1.2}
                                                className="w-min"
                                            >
                                                Seats Available
                                            </Typography>
                                        </div>
                                    </Grow>
                                </>
                            ) : (
                                <CircularProgress className="absolute top-1/2" />
                            )}
                        </CardContent>
                    </Card>
                    <Card className="rounded-lg col-span-3 min-[1600px]:col-span-2 min-w-[417px] w-full">
                        <CardContent className="flex flex-col items-center relative">
                            <Typography fontFamily="museo-sans" letterSpacing={-1} fontSize={20}>
                                Guest Activity
                            </Typography>
                            {!isActivityFetching ? (
                                <div className="mt-5 flex flex-col gap-3 w-full px-5 h-full">
                                    {guestActivity?.count ? (
                                        guestActivity?.results.map((activity, index) => {
                                            const activityState = getActivityState(activity.status);
                                            return (
                                                <div className="flex items-center gap-3" key={`activity-${index}`}>
                                                    {activity.user.profile_picture ? (
                                                        <Avatar
                                                            src={activity.user.profile_picture}
                                                            variant="square"
                                                            className="size-8 rounded-lg"
                                                        />
                                                    ) : (
                                                        <Avatar
                                                            {...stringAvatar(activity.user.full_name, {
                                                                fontSize: 'small',
                                                            })}
                                                            variant="square"
                                                            className="size-8 rounded-lg"
                                                        />
                                                    )}
                                                    <div className="w-full">
                                                        <Typography
                                                            color="gray-1.main"
                                                            fontWeight={500}
                                                            variant="body2"
                                                        >
                                                            {activity.user.full_name}
                                                        </Typography>
                                                        <Typography color="gray51.main" fontSize={12}>
                                                            {activity.user.email}
                                                        </Typography>
                                                    </div>
                                                    <div
                                                        className={clsx(
                                                            'rounded-full h-full flex justify-center items-center min-w-[116px] min-h-[38px]',
                                                            activityState.bgColor
                                                        )}
                                                    >
                                                        <Typography
                                                            fontSize={13}
                                                            fontWeight={600}
                                                            color={activityState.color}
                                                            textAlign="center"
                                                        >
                                                            {activityState.label}
                                                        </Typography>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <div className="flex items-center justify-center h-full">
                                            <Typography
                                                color="neutral-900.main"
                                                fontSize={16}
                                                fontWeight={500}
                                                borderRadius={4}
                                                padding="15px"
                                                className="bg-light-silver-60"
                                            >
                                                No guest activity available.
                                            </Typography>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <CircularProgress className="absolute top-1/2" />
                            )}
                        </CardContent>
                    </Card>
                </div>
                {hasRequiredBadges ? (
                    <div className="grid grid-cols-2 gap-[12px]">
                        <Card className="rounded-lg">
                            <CardContent className="pb-[12px]">
                                <Typography
                                    fontFamily="museo-sans"
                                    fontSize={20}
                                    letterSpacing={-1}
                                    maxWidth={170}
                                    lineHeight="110%"
                                >
                                    Prerequisite Events for Registration
                                </Typography>
                                <Typography fontSize={12} color="sonic-silver.main" maxWidth={400} mt={1}>
                                    Guests must have completed at least one of the following past events to be eligible
                                    to register, based on the organizer's rules.
                                </Typography>
                                {!statistics?.isLoading ? (
                                    <Typography color="primary" fontSize={48} fontWeight={700}>
                                        {statistics?.data?.events_rewarding_one_of_required_badges_count}
                                    </Typography>
                                ) : (
                                    <CircularProgress className="mt-5" />
                                )}
                            </CardContent>
                        </Card>
                        <Card className="rounded-lg">
                            <CardContent className="pb-[12px]">
                                <Typography
                                    fontFamily="museo-sans"
                                    fontSize={20}
                                    letterSpacing={-1}
                                    maxWidth={170}
                                    lineHeight="110%"
                                >
                                    Eligible Guests for Registration
                                </Typography>
                                <Typography fontSize={12} color="sonic-silver.main" maxWidth={450} mt={1}>
                                    This shows the number of users from the Victron Professional database who meet the
                                    organizer’s registration requirements for this event.
                                </Typography>
                                {!statistics?.isLoading ? (
                                    <Typography color="primary" fontSize={48} fontWeight={700}>
                                        {statistics?.data?.badge_eligible_users_count}
                                    </Typography>
                                ) : (
                                    <CircularProgress className="mt-5" />
                                )}
                            </CardContent>
                        </Card>
                    </div>
                ) : null}
            </AccordionDetails>
        </Accordion>
    );
};

export { EventAnalyticsAccordion };
